import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import type { UserPredictions } from '../../src/challenges/types/challengeCard.types';
import type { RequiredMarketsByMatch } from '../../src/challenges/helpers/acceptChallengePicks.helper';
import type { ChallengeOddsSnapshot } from '../../types/challenges';
import {
  challengeCardStyles as defaultStyles,
  type ChallengeCardStyles,
} from '../../src/challenges/styles/challengeCard.styles';
import { CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH } from '../../src/challenges/constants/challengeCardLayout.constants';
import { ChallengeMatchRow, type OtherPickDisplay, type Match } from './ChallengeMatchRow';

interface ChallengeCardMatchesProps {
  matches: Match[];
  userPredictions: UserPredictions;
  /** Other-market picks (BTTS, O/U) for current user; derived from challenge.picks (marketId !== 1). */
  otherPicks?: OtherPickDisplay[];
  /** Other participant's picks (for read-only "your pick vs their pick" display). */
  otherParticipantPicks?: OtherPickDisplay[];
  /** Opponent's 1X2 pick per match (value + odd + optional correct flag). Shown only when match is live/finished. */
  opponent1x2ByMatch?: Record<
    string,
    { value: 'home' | 'draw' | 'away'; odd: number; isCorrect?: boolean } | null
  >;
  /**
   * Current user's 1X2 decimal odd per match from challenge.picks (same source as opponent column).
   * When set, preferred over match-list / API odds so Create + guest Arena show the price at send time.
   */
  my1x2OddByMatch?: Record<string, number>;
  /** Current user's 1X2 correct/wrong per match (for finished/live). */
  my1x2CorrectByMatch?: Record<string, boolean>;
  currentUserId: string;
  showAllMatches: boolean;
  onShowAllMatches: () => void;
  /** When true (invitee view), show only required market names, no picks or odds. */
  showRequiredMarketsOnly?: boolean;
  /** Required markets per match from creator; used when showRequiredMarketsOnly. */
  requiredMarketsByMatch?: RequiredMarketsByMatch;
  /** Frozen odds from challenge creation; used to hide phantom 1X2 when that market was not offered. */
  oddsSnapshot?: ChallengeOddsSnapshot | null;
  /** When set (e.g. your turn to accept), pressing a "?" cell opens the accept modal. */
  onOpenPicksModal?: () => void;
  /** Theme-aware styles from parent. When omitted (e.g. Storybook), light theme styles are used. */
  styles?: ChallengeCardStyles;
  /** Test ID for testing */
  testID?: string;
}

const ChallengeCardMatchesInner: React.FC<ChallengeCardMatchesProps> = ({
  matches,
  userPredictions,
  otherPicks = [],
  otherParticipantPicks = [],
  opponent1x2ByMatch = {},
  my1x2OddByMatch = {},
  my1x2CorrectByMatch = {},
  currentUserId,
  showAllMatches,
  onShowAllMatches,
  showRequiredMarketsOnly = false,
  requiredMarketsByMatch = {},
  oddsSnapshot = null,
  onOpenPicksModal,
  styles = defaultStyles,
  testID,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const useConstrainedCard = screenWidth > CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH;

  const challengeHasOddsSnapshot = useMemo(
    () =>
      oddsSnapshot != null &&
      typeof oddsSnapshot === 'object' &&
      Object.keys(oddsSnapshot).length > 0,
    [oddsSnapshot]
  );

  const otherPicksByMatch = useMemo(() => {
    const map: Record<string, OtherPickDisplay[]> = {};
    otherPicks
      .filter((p) => p.userId === currentUserId)
      .forEach((p) => {
        if (!map[p.matchId]) map[p.matchId] = [];
        map[p.matchId].push(p);
      });
    return map;
  }, [otherPicks, currentUserId]);

  const otherParticipantPicksByMatch = useMemo(() => {
    const map: Record<string, OtherPickDisplay[]> = {};
    otherParticipantPicks.forEach((p) => {
      if (!map[p.matchId]) map[p.matchId] = [];
      map[p.matchId].push(p);
    });
    return map;
  }, [otherParticipantPicks]);

  const sortedMatches = useMemo(
    () => [...matches].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [matches]
  );

  if (sortedMatches.length === 0) {
    return (
      <View style={styles.noMatchesContainer} testID={testID ? `${testID}-no-matches` : 'challenge-card-matches-no-matches'}>
        <Text style={styles.noMatchesText}>
          No matches found for this challenge
        </Text>
        <Text style={styles.noMatchesSubtext}>Loading matches...</Text>
      </View>
    );
  }

  const displayedMatches = showAllMatches
    ? sortedMatches
    : sortedMatches.slice(0, Math.min(sortedMatches.length, 5));

  return (
    <View style={styles.compactMatchesWrapper} testID={testID || 'challenge-card-matches'}>
      {displayedMatches.map((match) => (
        <ChallengeMatchRow
          key={match.id}
          match={match}
          styles={styles}
          pick={userPredictions[match.id] ?? null}
          otherPicksForMatch={otherPicksByMatch[match.id] ?? []}
          otherParticipantPicksForMatch={otherParticipantPicksByMatch[match.id] ?? []}
          opponent1x2={opponent1x2ByMatch[match.id]}
          my1x2Odd={my1x2OddByMatch[match.id]}
          my1x2Correct={my1x2CorrectByMatch[match.id]}
          showRequiredMarketsOnly={showRequiredMarketsOnly}
          requiredMarkets={requiredMarketsByMatch[match.id] ?? []}
          challengeHasOddsSnapshot={challengeHasOddsSnapshot}
          oddsSnapshot={oddsSnapshot}
          useConstrainedCard={useConstrainedCard}
          onOpenPicksModal={onOpenPicksModal}
          testID={testID}
        />
      ))}

      {sortedMatches.length > 5 && !showAllMatches && (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.compactShowMore,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.compactShowMorePressed,
            hovered && Platform.OS === 'web' && styles.compactShowMoreHover,
          ]}
          onPress={onShowAllMatches}
        >
          <Text style={styles.compactShowMoreText}>
            Show {sortedMatches.length - 5} more match
            {sortedMatches.length - 5 !== 1 ? 'es' : ''}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

export const ChallengeCardMatches = React.memo(ChallengeCardMatchesInner);
